import type {
  CompletionResult,
  Interaction,
  PlayerHost,
  PlayerSnapshot
} from '@knolskape/educate-components';
import { loadPlayerProgress, savePlayerProgress } from '@/utils/playerProgress';
import type { EducateInteraction } from '@/types/educate';

/**
 * Bridges the shared player engine to 4e's module lifecycle. The engine stays
 * host-agnostic (no localStorage, no backend); this adapter is where 4e owns
 * resume (localStorage), persistence, and completion. Each product supplies how
 * the engine's completion maps onto its 4e unit ids via `mapCompletion`.
 */
export interface ModuleHostDeps {
  storylineId: string | null | undefined;
  onComplete: (completedUnitIds: string[], interactions: EducateInteraction[]) => void;
  /** Translate engine unit ids (e.g. `section-0`) to this module's 4e unit ids. */
  mapUnitId?: (engineUnitId: string) => string;
}

const toEducateInteraction = (
  interaction: Interaction,
  mapUnitId: (id: string) => string
): EducateInteraction => ({
  unitId: mapUnitId(interaction.unitId),
  kind: interaction.kind,
  answer: interaction.answer,
  isCorrect: interaction.isCorrect ?? null
});

export const buildPlayerHost = ({
  storylineId,
  onComplete,
  mapUnitId = (id) => id
}: ModuleHostDeps): PlayerHost => ({
  initialSnapshot: (loadPlayerProgress<PlayerSnapshot>(storylineId) as PlayerSnapshot) ?? null,
  onSnapshot: (snapshot) => savePlayerProgress(storylineId, snapshot),
  onComplete: (result: CompletionResult) => {
    onComplete(
      result.completedUnitIds.map(mapUnitId),
      result.interactions.map((interaction) => toEducateInteraction(interaction, mapUnitId))
    );
  }
});
